// src/components/ProfileSettingsPage.tsx

import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card.tsx";
import { Button } from "./ui/button.tsx";
import { Input } from "./ui/input.tsx";
import { Label } from "./ui/label.tsx";
import { Textarea } from "./ui/textarea.tsx";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs.tsx";
import { toast } from "sonner";
import { ArrowLeft, Upload, AlertTriangle, Trash2 } from "lucide-react";
import { ImageWithFallback } from "./resim/ImageWithFallback.tsx";

export function ProfileSettingsPage() {
  const navigate = useNavigate();
  const userId = localStorage.getItem("userId");
  const [loading, setLoading] = useState(true);
  
  // --- STATE TANIMLARI ---
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState(''); 
  
  const [phone, setPhone] = useState('');        
  const [city, setCity] = useState('');          
  const [linkedin, setLinkedin] = useState('');
  const [instagram, setInstagram] = useState('');
  const [bio, setBio] = useState('');            
  const [graduationYear, setGraduationYear] = useState(''); 
  const [department, setDepartment] = useState('');
  const [company, setCompany] = useState('');    
  const [occupation, setOccupation] = useState(''); 

  const [profileImagePreview, setProfileImagePreview] = useState('');
  const [coverImagePreview, setCoverImagePreview] = useState('');

  const [selectedProfileFile, setSelectedProfileFile] = useState<File | null>(null);
  const [selectedCoverFile, setSelectedCoverFile] = useState<File | null>(null);
  
  // Fotoğraf kaldırma durumları
  const [removeProfilePic, setRemoveProfilePic] = useState(false);
  const [removeCoverPic, setRemoveCoverPic] = useState(false);

  // Güvenlik Sekmesi State Tanımları
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const profileInputRef = useRef<HTMLInputElement>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!userId) {
      toast.error("Kullanıcı bulunamadı. Lütfen tekrar giriş yapın.");
      setLoading(false);
      return;
    }
    const fetchProfile = async () => {
      try {
        const response = await api.get(`/users/${userId}/`);
        const data = response.data;
        const profileDetails = data.profile || {}; 
        
        setFirstName(data.FirstName || '');
        setLastName(data.LastName || '');
        setEmail(data.email || ''); 
        
        setPhone(profileDetails.PhoneNumber || '');
        setCity(profileDetails.City || '');
        setBio(profileDetails.Biography || '');
        setGraduationYear(profileDetails.GraduationYear || '');
        setCompany(profileDetails.Company || '');
        setOccupation(profileDetails.Occupation || ''); 
        
        setProfileImagePreview(profileDetails.ProfilePicture || ''); 
        setCoverImagePreview(profileDetails.BackgroundPicture || '');
        setLinkedin(profileDetails.LinkedinLink || '');
        setInstagram(profileDetails.InstagramLink || '');
        
        setDepartment(data.Department || ''); 
      } catch (error) {
        console.error(error);
        toast.error("Profil verisi yüklenemedi.");
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [userId]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, type: "profile" | "cover") => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const previewUrl = URL.createObjectURL(file);

      if (type === "profile") {
        setSelectedProfileFile(file);
        setProfileImagePreview(previewUrl);
        setRemoveProfilePic(false);
      } else {
        setSelectedCoverFile(file);
        setCoverImagePreview(previewUrl);
        setRemoveCoverPic(false);
      }
    }
  };

  const triggerFileInput = (type: "profile" | "cover") => {
    if (type === "profile") profileInputRef.current?.click();
    else coverInputRef.current?.click();
  };

  const handleRemovePhoto = (type: "profile" | "cover") => {
    if (type === "profile") {
      setProfileImagePreview('');
      setSelectedProfileFile(null);
      setRemoveProfilePic(true);
    } else {
      setCoverImagePreview('');
      setSelectedCoverFile(null);
      setRemoveCoverPic(true);
    }
  };

  const handleProfileUpdate = async (e: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!userId) return;

    const formData = new FormData();
    formData.append("FirstName", firstName);
    formData.append("LastName", lastName);
    formData.append("email", email); 
    formData.append("Department", department); 
    formData.append("LinkedinLink", linkedin);
    formData.append("InstagramLink", instagram);
    formData.append("PhoneNumber", phone);
    formData.append("City", city);
    formData.append("Biography", bio);
    formData.append("Company", company);
    formData.append("Occupation", occupation); 
    formData.append("GraduationYear", graduationYear);

    if (selectedProfileFile) {
      formData.append("ProfilePicture", selectedProfileFile);
    } else if (removeProfilePic) {
      formData.append("remove_profile_picture", "true");
    }

    if (selectedCoverFile) {
      formData.append("BackgroundPicture", selectedCoverFile);
    } else if (removeCoverPic) {
      formData.append("remove_cover_picture", "true");
    }

    try {
      await api.patch(`/users/${userId}/`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Profil başarıyla güncellendi!");
    } catch (error: any) {
      console.error(error.response?.data);
      toast.error("Profil güncellemede hata oluştu.");
    }
  };

  // --- HESAP SİLME (Geri Getirildi) ---
  // --- HESAP SİLME (Şifre Onaylı) ---
  const handleDeleteAccount = async () => {
    if (!userId) return;

    const passwordInput = window.prompt(
      "Hesabınızı kalıcı olarak silmek istediğinize emin misiniz? Bu işlem geri alınamaz.\n\nOnaylamak için lütfen şifrenizi girin:"
    );

    if (!passwordInput) {
      toast.error("Hesap silme işlemi iptal edildi.");
      return;
    }

    try {
      // Backend'e silme isteği ile birlikte şifreyi de gönderiyoruz
      await api.delete(`/users/${userId}/`, {
        data: { password: passwordInput }
      });

      toast.success("Hesabınız kalıcı olarak silindi. Hoşçakalın 😢");

      localStorage.removeItem("access");
      localStorage.removeItem("refresh");
      localStorage.removeItem("userId");
      localStorage.removeItem("isStaff");

      window.location.href = "/";
    } catch (error: any) {
      console.error(error);
      const errorMsg = error.response?.data?.error || "Şifre hatalı veya hesap silinemedi!";
      toast.error(errorMsg);
    }
  };

  // --- ŞİFRE DEĞİŞTİRME (Geri Getirildi) ---
  // --- ŞİFRE DEĞİŞTİRME / OLUŞTURMA ---
  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId) return;

    if (newPassword !== confirmPassword) {
      toast.error("Yeni şifreler uyuşmuyor!");
      return;
    }

    try {
      // Backend'de yazdığımız /auth/set-password/ adresine istek atıyoruz
      await api.post('/auth/set-password/', {
        current_password: currentPassword || undefined, // Google ile gelenlerin eski şifresi yoksa burası boş gider, hata vermez
        new_password: newPassword,
      });

      toast.success("Şifreniz başarıyla oluşturuldu / güncellendi!");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error: any) {
      const errorMsg = error.response?.data?.error || "Şifre değiştirme başarısız!";
      toast.error(errorMsg);
    }
  };

  if (loading) return <div className="text-center py-10">Profil Yükleniyor...</div>;

  return (
    <div className="space-y-6">
      <input type="file" ref={profileInputRef} className="hidden" accept="image/*" onChange={(e) => handleFileChange(e, "profile")} />
      <input type="file" ref={coverInputRef} className="hidden" accept="image/*" onChange={(e) => handleFileChange(e, "cover")} />

      {/* HEADER */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/profil')} className="gap-2">
          <ArrowLeft className="h-4 w-4" />
          Profile Dön
        </Button>
        <div>
          <h1 className="text-xl font-bold text-slate-900">Profil Ayarları</h1>
          <p className="text-slate-500 text-sm">Profil bilgilerinizi yönetin</p>
          <div className="flex items-center gap-2 text-amber-600 bg-amber-50 px-3 py-1.5 rounded-lg text-xs font-medium mt-2 border border-amber-200/60">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span>Burada girdiğiniz bilgiler profilinizde görünüp mezun ağındaki diğer üyelerle paylaşılacaktır.</span>
          </div>
        </div>
      </div>

      <Tabs defaultValue="genel" className="w-full">
        <TabsList className="grid w-full max-w-2xl grid-cols-4">
          <TabsTrigger value="genel">Genel</TabsTrigger>
          <TabsTrigger value="fotograf">Fotoğraflar</TabsTrigger>
          <TabsTrigger value="kariyer">Kariyer</TabsTrigger>
          <TabsTrigger value="guvenlik">Güvenlik</TabsTrigger>
        </TabsList>

        {/* ---------------- GENEL ---------------- */}
        <TabsContent value="genel" className="mt-6">
          <Card>
            <CardContent className="pt-6">
              <form onSubmit={handleProfileUpdate} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Ad</Label><Input value={firstName} onChange={(e) => setFirstName(e.target.value)} /></div>
                  <div className="space-y-2"><Label>Soyad</Label><Input value={lastName} onChange={(e) => setLastName(e.target.value)} /></div>
                </div>

                <div className="space-y-2">
                  <Label>E-posta</Label>
                  <Input value={email} disabled className="bg-slate-100" />
                  <p className="text-xs text-muted-foreground">E-posta adresinizi değiştirmek için yönetici ile iletişime geçin.</p>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2"><Label>Telefon</Label><Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+90 5XX..." /></div>
                    <div className="space-y-2"><Label>Şehir</Label><Input value={city} onChange={(e) => setCity(e.target.value)} /></div>
                </div>

                <div className="space-y-2"><Label>LinkedIn</Label><Input placeholder="https://linkedin.com/in/..." value={linkedin} onChange={(e) => setLinkedin(e.target.value)} /></div>
                <div className="space-y-2"><Label>Instagram</Label><Input placeholder="https://instagram.com/..." value={instagram} onChange={(e) => setInstagram(e.target.value)} /></div>
                <div className="space-y-2"><Label>Hakkında</Label><Textarea value={bio} onChange={(e) => setBio(e.target.value)} rows={4} /></div>
                
                <Button type="submit">Değişiklikleri Kaydet</Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ---------------- FOTOĞRAFLAR ---------------- */}
        <TabsContent value="fotograf" className="mt-6">
          <div className="grid gap-6">
            <Card>
                <CardHeader><CardTitle>Profil Fotoğrafı</CardTitle></CardHeader>
                <CardContent className="flex flex-col md:flex-row items-center gap-6">
                <ImageWithFallback src={profileImagePreview} className="w-32 h-32 rounded-full object-cover border-2 border-slate-200 bg-white" alt="profile" />
                <div className="flex gap-2">
                    <Button type="button" variant="outline" onClick={() => triggerFileInput("profile")}>
                    <Upload className="h-4 w-4 mr-2" /> Değiştir
                    </Button>
                    <Button type="button" variant="destructive" onClick={() => handleRemovePhoto("profile")}>
                    <Trash2 className="h-4 w-4 mr-2" /> Kaldır
                    </Button>
                </div>
                </CardContent>
            </Card>

            <Card>
                <CardHeader><CardTitle>Kapak Fotoğrafı</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                <div className="w-full h-40 rounded-lg overflow-hidden border-2 border-slate-200 bg-slate-50">
                    <ImageWithFallback src={coverImagePreview} className="w-full h-full object-cover" alt="cover" />
                </div>
                <div className="flex gap-2">
                    <Button type="button" variant="outline" onClick={() => triggerFileInput("cover")}>
                    <Upload className="h-4 w-4 mr-2" /> Değiştir
                    </Button>
                    <Button type="button" variant="destructive" onClick={() => handleRemovePhoto("cover")}>
                    <Trash2 className="h-4 w-4 mr-2" /> Kaldır
                    </Button>
                </div>
                </CardContent>
            </Card>
            
            <div className="flex justify-end">
                <Button onClick={handleProfileUpdate}>Fotoğrafları Kaydet</Button>
            </div>
          </div>
        </TabsContent>

        {/* ---------------- KARİYER ---------------- */}
        <TabsContent value="kariyer" className="mt-6">
          <Card>
            <CardContent className="pt-6">
              <form onSubmit={handleProfileUpdate} className="space-y-4">
                <div className="space-y-2"><Label>Mezuniyet Yılı</Label><Input type="number" value={graduationYear} onChange={(e) => setGraduationYear(e.target.value)} /></div>
                <div className="space-y-2"><Label>Meslek / Ünvan</Label><Input value={occupation} onChange={(e) => setOccupation(e.target.value)} placeholder="Örn: Yazılım Mühendisi" /></div>
                <div className="space-y-2"><Label>Şirket / Kurum</Label><Input value={company} onChange={(e) => setCompany(e.target.value)} /></div>
                
                <Button type="submit">Kaydet</Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ---------------- GÜVENLİK (Eksiksiz Geri Getirildi) ---------------- */}
        <TabsContent value="guvenlik" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Şifre Değiştir</CardTitle>
              <CardDescription>Hesap güvenliğiniz için şifrenizi düzenli olarak güncelleyin.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div className="space-y-2">
                  <div className="space-y-2">
                  <Label>Mevcut Şifre</Label>
                  <p className="text-xs text-slate-500">
                    Google ile giriş yaptıysanız veya daha önce şifre oluşturmadıysanız bu alanı boş bırakabilirsiniz.
                  </p>
                </div>
                  <Input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} placeholder="••••••••" />
                </div>

                <div className="space-y-2">
                  <Label>Yeni Şifre</Label>
                  <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="••••••••" />
                </div>

                <div className="space-y-2">
                  <Label>Yeni Şifre Tekrar</Label>
                  <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="••••••••" />
                </div>
                
                <Button type="submit">Şifreyi Değiştir</Button>
                
                {/* 🚨 Tehlikeli Bölge - Hesabı Kalıcı Sil */}
                <Card className="border border-red-200 bg-red-50/40 mt-8 p-6">
                  <CardHeader className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 p-0">
                    <div className="space-y-1">
                      <CardTitle className="text-red-700 text-lg font-bold">Hesabını kalıcı olarak sil</CardTitle>
                      <CardDescription className="text-black/60 text-sm mt-1">
                        Bu işlem geri alınamaz. Hesabınız, profil verileriniz ve tüm bağlantılarınız sistemden kalıcı olarak temizlenir.
                      </CardDescription>
                    </div>

                    <Button
                      type="button"
                      variant="destructive"
                      className="w-full md:w-auto rounded-xl shadow-sm font-medium"
                      onClick={handleDeleteAccount}
                    >
                      Hesabımı Sil
                    </Button>
                  </CardHeader>
                </Card>
              </form>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}